
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Building2, 
  Calendar, 
  DollarSign, 
  Edit, 
  Trash, 
  User
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { OpportunityWithRelations } from "@/types/custom-types";
import { Button } from "@/components/ui/button";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

interface OpportunityHeaderProps {
  opportunity: OpportunityWithRelations;
  onDelete: (id: string) => void;
  isDeleting?: boolean;
}

export function OpportunityHeader({ opportunity, onDelete, isDeleting = false }: OpportunityHeaderProps) {
  const navigate = useNavigate();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString();
  };

  const handleDelete = () => {
    onDelete(opportunity.id);
    navigate("/opportunities");
  };

  let badgeVariant: "default" | "outline" | "secondary" | "destructive" = "secondary";
  
  switch ((opportunity.stage || "").toLowerCase()) {
    case 'qualified':
      badgeVariant = "outline";
      break;
    case 'proposal':
      badgeVariant = "secondary";
      break;
    case 'negotiation':
      badgeVariant = "default";
      break;
    case 'closed won':
      badgeVariant = "default";
      break;
    case 'closed lost':
      badgeVariant = "destructive";
      break;
    default:
      badgeVariant = "secondary";
  }

  return (
    <div className="pb-4 border-b">
      <div className="flex items-center mb-2">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(-1)} 
          className="mr-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <Badge variant={badgeVariant} className="ml-auto">
          {opportunity.stage}
        </Badge>
      </div>
      
      <div className="flex justify-between items-start mb-4">
        <h1 className="text-2xl font-bold">{opportunity.name}</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/opportunities/${opportunity.id}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={isDeleting}
          >
            <Trash className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
        <div className="flex items-center">
          <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
          <div>
            <div className="text-muted-foreground">Organization</div>
            {opportunity.organization ? (
              <div className="font-medium">
                <a 
                  href={`/organizations/${opportunity.organization.id}`}
                  className="hover:underline"
                >
                  {opportunity.organization.name}
                </a>
              </div>
            ) : (
              <div>—</div>
            )}
          </div>
        </div>
        
        <div className="flex items-center">
          <User className="h-4 w-4 mr-2 text-muted-foreground" />
          <div>
            <div className="text-muted-foreground">Contact</div>
            {opportunity.contact ? (
              <div className="font-medium">
                <a 
                  href={`/contacts/${opportunity.contact.id}`}
                  className="hover:underline"
                >
                  {opportunity.contact.first_name} {opportunity.contact.last_name}
                </a>
              </div>
            ) : (
              <div>—</div>
            )}
          </div>
        </div>
        
        <div className="flex items-center">
          <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
          <div>
            <div className="text-muted-foreground">Expected Close</div>
            <div className="font-medium">{formatDate(opportunity.expected_close_date)}</div>
          </div>
        </div>
        
        <div className="flex items-center">
          <DollarSign className="h-4 w-4 mr-2 text-muted-foreground" />
          <div>
            <div className="text-muted-foreground">Value</div>
            <div className="font-medium">
              {opportunity.value ? formatCurrency(opportunity.value) : "—"}
              {opportunity.probability ? ` · ${opportunity.probability}%` : ""}
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the opportunity "{opportunity.name}".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
